import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { Coordinate, Square } from '@/api/types';

/*
 * The landing page's board, told as a story rather than shown as a picture.
 *
 * This is the campaign video on the app's own grid — the same beats as `TIMELINE` in
 * apps/web/scripts/ad-video.mjs, which is the piece of work that proved the point: a *finished*
 * board is a diagram somebody has to decode, and a board that fills up in front of you explains
 * the game without a word of instruction. The page used to open on the finished one.
 *
 * **`revealAt` is a pure function of time**, for the same reason `frame(t)` is over there. The
 * board it rests on is the board the page has always drawn, produced by the same code that
 * animates towards it, so the illustration cannot drift from the animation that lands on it.
 * Everything worth asserting is asserted against that function; the driver below is only the
 * clock, the "are we on screen yet", and the cleanup.
 */

/** The reader, in the example. Their squares are drawn as *yours* — dark green, as in a game. */
export const DEMO_OWNER_ID = 'demo-1';

/** The square the page's copy names, and the fixture it names it from. Springboks 12, All Blacks 34. */
export const DEMO_WINNER: Coordinate = { col: 2, row: 4 };
export const DEMO_SCORE = { a: 12, b: 34 } as const;

/*
 * The fixture, drawn the way a real game draws it: two named bars in the teams' own colours.
 *
 * Not translated, because they are names. The page's own copy speaks about them in each
 * language; the board labels them the same everywhere, exactly as a game between these two would
 * on any of the three.
 */
export const DEMO_TEAMS = {
  a: 'Springboks',
  b: 'All Blacks',
  aColour: 'gold',
  bColour: 'black',
} as const;

/*
 * The example board — **all one hundred squares**, because a board with a gap in it is a board
 * somebody could still have bought into, and the last frame of this story is a pool that is
 * closed. Eleven players: the reader, who takes ten, and ten others taking nine each.
 *
 * The names are ordinary South African first names and nothing more, because that is who plays
 * this. They are not translated, for the same reason the team bars are not: a name is a name in
 * every language.
 *
 * **Two things about them are deliberate and neither is visible in the data.** The ten are five
 * men and five women, and five Black and five white South African names — this is a game played
 * in a room, and the room should look like one. And every one of them is four characters, which
 * is a constraint the board imposes rather than a preference: this board fits the phone whole
 * instead of scrolling, so its cells are about a third of a played board's, and a longer name
 * came back as three letters and an ellipsis — ninety of them, which is a board of strangers.
 * test/e2e/board-legibility.spec.ts measures that on a real phone viewport, and the length is
 * pinned in test/unit/useHomeReveal.test.ts so a fifth name cannot quietly undo it.
 *
 * Ordered rather than merely listed. The reader's ten come first because the story lands them
 * first, and the winning square is the first of those — the copy names it, so it cannot arrive
 * halfway through a scatter. Everybody else's are shuffled *across* the ten of them rather than
 * grouped, which is what makes the second chapter read as a room filling up instead of ten
 * people taking turns.
 *
 * Fixed rather than random, like the artwork's board: an illustration that redraws differently
 * on every visit cannot be pointed at by a caption that names one square. It was generated once
 * from a seeded shuffle and then frozen here.
 */
export const EXAMPLE_SQUARES: readonly Square[] = [
  { col: 2, row: 4, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 8, row: 9, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 0, row: 0, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 2, row: 0, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 7, row: 3, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 1, row: 0, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 5, row: 0, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 6, row: 7, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 6, row: 8, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },
  { col: 2, row: 1, subjectId: DEMO_OWNER_ID, displayName: 'You', free: false },

  { col: 9, row: 6, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 1, row: 1, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 8, row: 4, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 1, row: 2, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 1, row: 7, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 7, row: 8, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 7, row: 2, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 0, row: 1, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 2, row: 6, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 9, row: 3, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 0, row: 2, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 6, row: 9, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 4, row: 0, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 4, row: 6, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 7, row: 7, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 0, row: 8, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 3, row: 4, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 6, row: 4, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 8, row: 6, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 9, row: 9, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 3, row: 7, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 8, row: 1, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 0, row: 3, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 5, row: 6, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 6, row: 1, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 4, row: 7, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 3, row: 1, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 5, row: 9, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 4, row: 4, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 6, row: 6, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 1, row: 6, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 5, row: 8, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 8, row: 7, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 1, row: 8, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 1, row: 4, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 5, row: 2, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 7, row: 6, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 0, row: 4, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 1, row: 5, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 8, row: 2, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 2, row: 3, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 7, row: 1, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 7, row: 0, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 8, row: 3, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 8, row: 0, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 4, row: 8, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 8, row: 8, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 2, row: 9, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 4, row: 3, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 5, row: 4, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 3, row: 8, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 9, row: 8, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 1, row: 3, subjectId: 'demo-10', displayName: 'Lize', free: false },
  { col: 2, row: 2, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 7, row: 4, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 6, row: 2, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 3, row: 2, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 6, row: 3, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 2, row: 7, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 6, row: 5, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 4, row: 5, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 5, row: 5, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 0, row: 6, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 7, row: 9, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 3, row: 6, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 4, row: 9, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 0, row: 5, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 6, row: 0, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 9, row: 1, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 9, row: 7, subjectId: 'demo-11', displayName: 'Tumi', free: false },
  { col: 5, row: 7, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 9, row: 5, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 9, row: 0, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 0, row: 7, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 3, row: 3, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 9, row: 2, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 3, row: 0, subjectId: 'demo-2', displayName: 'Jaco', free: false },
  { col: 4, row: 2, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 2, row: 5, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 8, row: 5, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 4, row: 1, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 5, row: 1, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 5, row: 3, subjectId: 'demo-9', displayName: 'Anke', free: false },
  { col: 9, row: 4, subjectId: 'demo-3', displayName: 'Ruan', free: false },
  { col: 0, row: 9, subjectId: 'demo-4', displayName: 'Musa', free: false },
  { col: 2, row: 8, subjectId: 'demo-6', displayName: 'Tebo', free: false },
  { col: 3, row: 9, subjectId: 'demo-8', displayName: 'Mari', free: false },
  { col: 7, row: 5, subjectId: 'demo-5', displayName: 'Vusi', free: false },
  { col: 1, row: 9, subjectId: 'demo-7', displayName: 'Lebo', free: false },
  { col: 3, row: 5, subjectId: 'demo-3', displayName: 'Ruan', free: false },
];

const YOURS = EXAMPLE_SQUARES.filter((s) => s.subjectId === DEMO_OWNER_ID);
const THEIRS = EXAMPLE_SQUARES.filter((s) => s.subjectId !== DEMO_OWNER_ID);

/*
 * The beats, in milliseconds. Roughly the ad's eight seconds, tightened: a feed thumb gives that
 * ad one pass, while somebody on this page is already here and can be got to the point sooner.
 *
 * Your own squares land slowly, one at a time — that chapter is the one saying *you* do
 * something, and ten squares appearing at once says nothing. Everybody else's ninety come in
 * threes and much faster, which is what turns the second chapter into a room filling up rather
 * than a second list. Three at a time rather than one: ninety timers is ninety re-renders of a
 * hundred cells, and at the speed the chapter needs, a batch is what a scatter looks like anyway.
 */
export const MARKS = {
  /** The reader's first square. The caption is already up: it says what is about to happen. */
  yoursFrom: 400,
  yoursStep: 150,
  /** "Your buddies take theirs" — said before any of them lands. */
  theirs: 2000,
  theirsFrom: 2300,
  theirsStep: 75,
  /** How many of everybody else's land together. See the note above. */
  theirsPerStep: 3,
  /**
   * The final score. The whistle goes before anybody works out whose square it was.
   *
   * It holds for the best part of two seconds, unlike the two lines before it: those are
   * captions on something the eye is already following, and this one is a *number* that has to
   * be read and then matched against two axes. At the length of the other beats it flashed.
   */
  score: 4700,
  /**
   * The words go.
   *
   * They sit in the middle of the board and so does the winning square, so the reveal is the one
   * moment nothing may be in front of it. The ad has nothing on screen at that moment either —
   * the tile lighting up *is* the line.
   */
  clear: 6500,
  /** The crosshair, and the winning square. */
  won: 6800,
  /** The story is over, and the caption under the board can name the result again. */
  end: 7800,
} as const;

/** The three lines of the story. There is no fourth: the gold square is the fourth. */
export type RevealChapter = 'yours' | 'theirs' | 'score';

export interface RevealState {
  /** The line to show over the board, or nothing — see `MARKS.clear`. */
  caption: RevealChapter | null;
  /** The squares taken so far, in the order they were taken. */
  squares: Square[];
  /** The winning square, once the score has landed. The grid draws the crosshair from it. */
  winning: Coordinate | null;
  /** The final score, once the whistle has gone. */
  score: { a: number; b: number } | null;
  /** The board has finished filling and is resting. */
  done: boolean;
}

/** How many of a chapter's squares are down at `ms`, landing `perStep` of them at a time. */
function taken(ms: number, from: number, step: number, total: number, perStep = 1): number {
  if (ms < from) return 0;
  return Math.min(total, (Math.floor((ms - from) / step) + 1) * perStep);
}

/** The whole storyboard, as a function of time. Nothing accumulates; nothing is random. */
export function revealAt(ms: number): RevealState {
  const won = ms >= MARKS.won;
  const scored = ms >= MARKS.score;

  return {
    caption: ms >= MARKS.clear ? null : scored ? 'score' : ms >= MARKS.theirs ? 'theirs' : 'yours',
    squares: [
      ...YOURS.slice(0, taken(ms, MARKS.yoursFrom, MARKS.yoursStep, YOURS.length)),
      ...THEIRS.slice(
        0,
        taken(ms, MARKS.theirsFrom, MARKS.theirsStep, THEIRS.length, MARKS.theirsPerStep),
      ),
    ],
    winning: won ? DEMO_WINNER : null,
    score: scored ? { ...DEMO_SCORE } : null,
    done: ms >= MARKS.end,
  };
}

/*
 * Every instant at which the board changes, and the only values the driver ever puts on the
 * clock. A square landing is a step, not a slide, so there is nothing to interpolate between two
 * of these — which is why this can be a handful of timers rather than a frame loop that spends
 * most of its wake-ups deciding nothing has happened. The fades are CSS's job.
 */
export const TIMELINE: readonly number[] = [
  ...new Set([
    0,
    ...YOURS.map((_, i) => MARKS.yoursFrom + i * MARKS.yoursStep),
    MARKS.theirs,
    // One instant per *batch*, not per square: the ones in between draw nothing new.
    ...THEIRS.filter((_, i) => i % MARKS.theirsPerStep === 0).map(
      (_, i) => MARKS.theirsFrom + i * MARKS.theirsStep,
    ),
    MARKS.score,
    MARKS.clear,
    MARKS.won,
    MARKS.end,
  ]),
].sort((a, b) => a - b);

/** Somebody who has asked for less movement gets the finished board, not a faster animation. */
const reducedMotion = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

function motionUnwanted(): boolean {
  return reducedMotion()?.matches === true;
}

export interface UseHomeReveal {
  state: ComputedRef<RevealState>;
  /**
   * The story has been told, all the way to `MARKS.end`.
   *
   * **Not the same question as `state.done`**, and the difference is why this exists: the board
   * *rests* on its last frame until somebody scrolls to it, so `done` is true from the first
   * paint. Anything cued off the *end* of the story has to wait for a story to have happened —
   * so this stays false while the board is only resting, and is true from the start where there
   * is no story to wait for (reduced motion gets the finished board and nothing else).
   */
  told: Ref<boolean>;
}

/**
 * Plays the storyboard once, when the middle of `board` is on screen.
 *
 * **It starts on the finished board and rewinds.** An empty grid is what somebody who never
 * scrolls that far is left looking at, and an empty grid reads as a page that failed to load —
 * so the resting illustration is the full board this page has always shown, and the story only
 * winds it back once somebody is actually looking at it. Half the board visible is the trigger,
 * because the words land in the middle of it.
 *
 * Where nothing can say what is visible — jsdom, an old browser — it plays immediately: a board
 * that never fills is the worse failure.
 */
export function useHomeReveal(board: Ref<HTMLElement | null>): UseHomeReveal {
  const elapsed = ref(0);
  const told = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let observer: IntersectionObserver | undefined;

  const stop = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    observer?.disconnect();
    observer = undefined;
  };

  /** Walks TIMELINE, one timer at a time, each armed for the gap to the next instant. */
  const play = (from = 0): void => {
    const next = TIMELINE[from + 1];
    // Nothing left to draw: the last beat has been played, rather than merely being on screen.
    if (next === undefined) {
      told.value = true;
      return;
    }
    timer = setTimeout(
      () => {
        elapsed.value = next;
        play(from + 1);
      },
      next - (TIMELINE[from] ?? 0),
    );
  };

  if (motionUnwanted()) {
    elapsed.value = MARKS.end;
    // There is no story to wait for, so whatever follows one is part of the resting state.
    told.value = true;
  } else if (typeof IntersectionObserver === 'undefined') {
    // jsdom, or a browser too old to say what is on screen: play from the top, now.
    play();
  } else {
    // Resting on the last frame until somebody looks at it — the finished board is the fallback
    // illustration, not an empty grid.
    elapsed.value = MARKS.end;
    watch(
      board,
      (el) => {
        if (!el || observer) return;
        observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            // Once. It plays through and never needs telling again.
            observer?.disconnect();
            observer = undefined;
            // Rewind, then tell it. The board people watch fill has to start empty.
            elapsed.value = 0;
            play();
          },
          // Half of it: the words land in the middle of the board, so half on screen is the
          // point at which there is something to read.
          { threshold: 0.5 },
        );
        observer.observe(el);
      },
      { immediate: true },
    );
  }

  /*
   * ...and if the setting changes while the page is open, the board stops where it is and shows
   * the finished one. It used to be read once at setup and never again, so turning "reduce
   * motion" on did nothing until a reload — a small window, because the story plays once, and a
   * few lines to close.
   */
  const query = reducedMotion();
  const onMotionChange = (): void => {
    if (!motionUnwanted()) return;
    stop();
    elapsed.value = MARKS.end;
    told.value = true;
  };
  // Feature-checked rather than assumed: Safari before 14 had `addListener` and no
  // `addEventListener` on a MediaQueryList, and the setting still applies without the
  // subscription — it is read once at setup either way.
  const subscribes = typeof query?.addEventListener === 'function';
  if (subscribes) query?.addEventListener('change', onMotionChange);

  onScopeDispose(() => {
    if (subscribes) query?.removeEventListener('change', onMotionChange);
    stop();
  });

  return { state: computed(() => revealAt(elapsed.value)), told };
}
